// Aba "Carteira" do cliente: os cartões salvos na wallet do payment
// (GET /customers/{id}/cards), com exclusão pela lixeira.
//
// Só cartões por enquanto: loyalty points não tem endpoint no payment e o
// balance está com o código comentado lá — os dois ficaram no backlog.
//
// PCI: a resposta traz BIN + últimos 4 dígitos e NUNCA o PAN completo. Não
// adicione aqui nada que exiba número inteiro de cartão ou CVV.
import {
  CreditCardIcon,
  StarIcon,
  Trash2Icon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  useCustomerCards,
  useDeleteCustomerCardMutation,
} from "@/hooks/use-customer-cards"
import type { CustomerCard } from "@/types/customer-wallet"

const prefix = "pages.customers.details.wallet"

// Status que o payment usa para cartão utilizável; o resto é histórico.
const ACTIVE_STATUS = "ACTIVE"
const EXCLUDED_STATUS = "EXCLUDED"

type WalletTabProps = {
  customerId: string
}

/** "•••• 1111" — só os últimos 4; o PAN completo não existe no contrato. */
const maskedNumber = (card: CustomerCard) =>
  card.lastFourDigits
    ? `•••• ${card.lastFourDigits}`
    : "••••"

const expirationOf = (card: CustomerCard) =>
  card.expMonth && card.expYear
    ? `${card.expMonth}/${card.expYear}`
    : null

const statusToneOf = (status?: string | null) => {
  if (status === ACTIVE_STATUS)
    return "border-emerald-200 bg-emerald-50 text-emerald-700"
  if (status === EXCLUDED_STATUS)
    return "border-gray-200 bg-gray-50 text-gray-700"
  return "border-amber-200 bg-amber-50 text-amber-700"
}

export const WalletTab = ({
  customerId,
}: WalletTabProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useCustomerCards(customerId)
  const deleteMutation =
    useDeleteCustomerCardMutation(customerId)

  const [cardToDelete, setCardToDelete] =
    useState<CustomerCard | null>(null)

  if (isLoading) {
    return (
      <div
        data-testid="customer-cards-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton
            key={`customer-cards-skeleton-${index}`}
            className="h-24 w-full rounded-lg"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${prefix}.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${prefix}.retry`)}
        </Button>
      </div>
    )
  }

  const cards = data ?? []

  if (cards.length === 0) {
    return (
      <div
        data-testid="customer-cards-empty"
        className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center"
      >
        <CreditCardIcon
          className="size-6 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-sm font-medium">
          {t(`${prefix}.empty.title`)}
        </p>
        <p className="text-xs text-muted-foreground">
          {t(`${prefix}.empty.description`)}
        </p>
      </div>
    )
  }

  const handleConfirmDelete = () => {
    if (cardToDelete) {
      deleteMutation.mutate(cardToDelete.id)
    }
    setCardToDelete(null)
  }

  return (
    <div className="space-y-3">
      <ul className="flex flex-col gap-3">
        {cards.map((card) => {
          const isExcluded = card.status === EXCLUDED_STATUS

          return (
            <li
              key={card.id}
              data-testid={`customer-card-${card.id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4 shadow-sm"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
                  {card.brand?.logoUrl ? (
                    <img
                      src={card.brand.logoUrl}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <CreditCardIcon
                      className="size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                </div>

                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                    <span className="tabular-nums">
                      {maskedNumber(card)}
                    </span>
                    {card.brand?.name && (
                      <span className="text-xs font-normal text-muted-foreground">
                        {card.brand.name}
                      </span>
                    )}
                    {card.principal && (
                      <Badge
                        variant="outline"
                        data-testid={`customer-card-principal-${card.id}`}
                        className="gap-1 rounded-full border border-blue-200 bg-blue-50 font-medium text-blue-700"
                      >
                        <StarIcon
                          className="size-3"
                          aria-hidden="true"
                        />
                        {t(`${prefix}.principal`)}
                      </Badge>
                    )}
                    {card.status && (
                      <Badge
                        variant="outline"
                        className={`rounded-full font-medium ${statusToneOf(card.status)}`}
                      >
                        {t(
                          `${prefix}.status.${card.status}`,
                          { defaultValue: card.status }
                        )}
                      </Badge>
                    )}
                  </span>

                  <span className="truncate text-xs text-muted-foreground">
                    {card.holder?.name ?? "—"}
                    {expirationOf(card) && (
                      <>
                        {" · "}
                        {t(`${prefix}.expiresAt`)}{" "}
                        <span className="tabular-nums">
                          {expirationOf(card)}
                        </span>
                      </>
                    )}
                    {card.bin && (
                      <>
                        {" · BIN "}
                        <span className="tabular-nums">
                          {card.bin}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>

              {/* Cartão já excluído não tem o que excluir de novo. */}
              {!isExcluded && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  disabled={deleteMutation.isPending}
                  aria-label={t(`${prefix}.deleteAria`, {
                    card: maskedNumber(card),
                  })}
                  onClick={() => setCardToDelete(card)}
                >
                  <Trash2Icon
                    className="size-4"
                    aria-hidden="true"
                  />
                </Button>
              )}
            </li>
          )
        })}
      </ul>

      {/* Exclusão é irreversível pela tela — nunca silenciosa. */}
      <AlertDialog
        open={cardToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setCardToDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${prefix}.deleteDialog.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(`${prefix}.deleteDialog.description`, {
                card: cardToDelete
                  ? maskedNumber(cardToDelete)
                  : "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${prefix}.deleteDialog.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
            >
              {t(`${prefix}.deleteDialog.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
